import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { Navigate, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { billingApi, billingEnabled } from './api';
import PlanCards from './PlanCards';
import SignupShell from '../components/SignupShell';

export default function BillingSetup({ expired = false }) {
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const load = useCallback(async () => {
    try {
      setError('');
      const access = await billingApi.status();
      if (access.service_state === 'active') { navigate('/', { replace: true }); return; }
      if (!access.can_complete_setup) { setError('Your firm’s owner needs to choose a plan.'); return; }
      if (access.service_state === 'plan_required' && !expired) { navigate('/choose-plan', { replace: true }); return; }
      if (access.service_state === 'pending_setup' && expired) { navigate('/billing-setup', { replace: true }); return; }
      const value = await billingApi.account();
      setAccount(value); setSelected(value.signup?.selected_plan ?? null);
    } catch (failure) { setError(failure.message); }
  }, [navigate, expired]);
  useEffect(() => { if (billingEnabled && Cookies.get('token')) load(); }, [load]);
  const save = async plan => {
    if (inFlight.current || !plan) return;
    inFlight.current = true; setBusy(true); setError('');
    try {
      await (expired ? billingApi.chooseRequiredPlan : billingApi.chooseSignupPlan)(plan?.version_key ?? null);
      navigate(expired ? '/' : '/billing-payment', { replace: expired });
    } catch (failure) { setError(failure.message); }
    finally { inFlight.current = false; setBusy(false); }
  };
  if (!billingEnabled) return <Navigate to="/" replace />;
  if (!Cookies.get('token') || !Cookies.get('agencyId')) return <Navigate to="/signin" replace />;
  return <SignupShell wide><Stack spacing={3}>
    {error && <Alert severity="error" action={!account && <Button onClick={load}>Retry</Button>}>{error}</Alert>}
    {!account && !error && <CircularProgress aria-label="Loading plans" />}
    {account && <>
      <Box><Typography component="h1" fontSize={{ xs: 24, sm: 30 }} fontWeight={600} color="primary.heading">
        {expired ? 'Choose a plan to continue.' : 'Choose the right plan for your firm.'}
      </Typography>{expired && <Typography color="text.secondary" mt={1}>Billing and client access are paused until you choose a plan.</Typography>}</Box>
      <PlanCards plans={account.available_plans} selected={selected?.version_key} onSelect={setSelected} disabled={busy} />
      <Stack spacing={1.5} alignItems="center">
        {selected && <Button variant="contained" size="large" disabled={busy} onClick={() => save(selected)} sx={{ minWidth: { xs: '100%', sm: 320 }, minHeight: 48 }}>
          {busy ? 'Saving…' : expired ? `Start ${selected.display_name}` : `Continue with ${selected.display_name}`}
        </Button>}
      </Stack>
    </>}
  </Stack></SignupShell>;
}
